import { GroupSecurity } from '@quromedical/fhir-common';
import type { Location } from '@quromedical/models';
import { LocationFormCard } from 'core/location';
import {
  Alert,
  Section,
  Skeleton,
  SkeletonProvider,
  Snackbar,
} from 'design-system';
import { useLocalSearchParams } from 'expo-router';
import { useUserSession } from 'hooks/useUserSession';
import { organizationFetcher } from 'integration';
import type { DeviceStackScreenProps } from 'navigation';
import type React from 'react';
import { useCallback } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { strings } from 'strings';
import { trpc } from 'trpc';

type RouteParams = {
  id: string;
};

export const LocationScreen: React.FC<DeviceStackScreenProps<'View'>> = () => {
  const id = useLocalSearchParams<RouteParams>()?.id;

  const userSession = useUserSession();

  const canEdit = userSession.hasAny([GroupSecurity.LocationUpdate]);

  const orgFetcher = organizationFetcher();

  const {
    data = {},
    isFetching: isValidating,
    isError: error,
    refetch,
  } = trpc.location.getById.useQuery({ id });

  const revalidate = useCallback(async () => {
    await refetch();
    await userSession.getGroups();
  }, [userSession, refetch]);

  return (
    <ScrollView>
      <SkeletonProvider loading={isValidating}>
        <Section>
          <Skeleton>
            <LocationFormCard
              canEdit={canEdit}
              revalidate={revalidate}
              data={data as Location}
              id={id}
              organizationFetcher={orgFetcher}
            />
          </Skeleton>
          <Snackbar isOpen={error} onClose={revalidate}>
            <Alert
              actionIcon="close"
              backgroundColor="status-critical"
              title={strings.AlertError}
              onAction={revalidate}
              body={strings.DeviceCreateError}
            />
          </Snackbar>
        </Section>
      </SkeletonProvider>
    </ScrollView>
  );
};
